import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Button from './ui/Button';
import { Field, Label, Input, Textarea, Select, HelperText } from './ui/FormControls';
import { ErrorMessage, SuccessMessage, Spinner } from './ui/Feedback';
import { api } from '../api/client';
import { useSocket } from '../context/SocketContext';

const FormWrap = styled.form`
  max-width: 860px;
  margin: 0 auto;
`;

const Row = styled.div`
  display: grid;
  gap: 0 1.25rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const SubmitRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 0.5rem;

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-top: 1px solid ${({ theme }) => theme.colors.border};
  }
`;

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

const initialForm = {
  nombre: '',
  telefono: '',
  correo: '',
  fecha: todayISO(),
  hora: '',
  personas: 2,
  comentarios: ''
};

function ReservationForm() {
  const socket = useSocket();
  const [form, setForm] = useState(initialForm);
  const [availability, setAvailability] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const subscribedFecha = useRef(null);

  const loadAvailability = (fecha) => {
    setLoadingAvailability(true);
    api
      .get(`/api/reservations/availability?fecha=${fecha}`)
      .then((data) => setAvailability(data))
      .catch(() => setAvailability([]))
      .finally(() => setLoadingAvailability(false));
  };

  useEffect(() => {
    loadAvailability(form.fecha);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.fecha]);

  useEffect(() => {
    if (!socket) return undefined;

    if (subscribedFecha.current && subscribedFecha.current !== form.fecha) {
      socket.emit('availability:unsubscribe', subscribedFecha.current);
    }
    socket.emit('availability:subscribe', form.fecha);
    subscribedFecha.current = form.fecha;

    const handleUpdate = (payload) => {
      if (payload.fecha === form.fecha) {
        setAvailability(payload.availability);
      }
    };

    socket.on('availability:update', handleUpdate);

    return () => {
      socket.off('availability:update', handleUpdate);
    };
  }, [socket, form.fecha]);

  useEffect(() => {
    return () => {
      if (socket && subscribedFecha.current) {
        socket.emit('availability:unsubscribe', subscribedFecha.current);
      }
    };
  }, [socket]);

  const handleChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(null);

    if (!form.hora) {
      setError('Selecciona un horario disponible.');
      return;
    }

    setSubmitting(true);
    try {
      const data = await api.post('/api/reservations', {
        ...form,
        personas: Number(form.personas)
      });
      setSuccess(data.mensaje);
      setForm({ ...initialForm, fecha: form.fecha });
      loadAvailability(form.fecha);
    } catch (err) {
      setError(err.message);
      loadAvailability(form.fecha);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormWrap onSubmit={handleSubmit}>
      {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
      {success && <SuccessMessage role="status">{success}</SuccessMessage>}

      <Row>
        <Field>
          <Label htmlFor="rf-nombre">Nombre completo</Label>
          <Input
            id="rf-nombre"
            required
            value={form.nombre}
            onChange={handleChange('nombre')}
            placeholder="Tu nombre"
          />
        </Field>
        <Field>
          <Label htmlFor="rf-correo">Correo (opcional)</Label>
          <Input
            id="rf-correo"
            type="email"
            value={form.correo}
            onChange={handleChange('correo')}
            placeholder="tu@correo.com"
          />
        </Field>
        <Field>
          <Label htmlFor="rf-telefono">Teléfono</Label>
          <Input
            id="rf-telefono"
            required
            type="tel"
            value={form.telefono}
            onChange={handleChange('telefono')}
            placeholder="777 000 0000"
          />
        </Field>
      </Row>

      <Row>
        <Field>
          <Label htmlFor="rf-fecha">Fecha</Label>
          <Input
            id="rf-fecha"
            required
            type="date"
            min={todayISO()}
            value={form.fecha}
            onChange={handleChange('fecha')}
          />
        </Field>
        <Field>
          <Label htmlFor="rf-hora">Horario</Label>
          {loadingAvailability ? (
            <Spinner />
          ) : (
            <Select id="rf-hora" required value={form.hora} onChange={handleChange('hora')}>
              <option value="">Selecciona un horario</option>
              {availability.map((slot) => (
                <option key={slot.hora} value={slot.hora} disabled={!slot.disponible}>
                  {slot.hora.slice(0, 5)}{' '}
                  {slot.disponible
                    ? `(${slot.lugares_disponibles} lugares)`
                    : '(Sin disponibilidad)'}
                </option>
              ))}
            </Select>
          )}
          <HelperText>Disponibilidad en tiempo real, sin refrescar.</HelperText>
        </Field>
        <Field>
          <Label htmlFor="rf-personas">Número de personas</Label>
          <Input
            id="rf-personas"
            required
            type="number"
            min="1"
            max="30"
            value={form.personas}
            onChange={handleChange('personas')}
          />
        </Field>
      </Row>

      <Field>
        <Label htmlFor="rf-comentarios">Comentarios u ocasión especial</Label>
        <Textarea
          id="rf-comentarios"
          value={form.comentarios}
          onChange={handleChange('comentarios')}
          placeholder="Aniversario de tronada, cumpleaños, o solo ganas de despecharte..."
        />
      </Field>

      <SubmitRow>
        <Button type="submit" $variant="dark" disabled={submitting}>
          {submitting ? <Spinner $size="18px" /> : 'Enviar solicitud de reservación'}
        </Button>
      </SubmitRow>
    </FormWrap>
  );
}

export default ReservationForm;
